import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { MapPin, Video } from 'lucide-react';
import { api } from '../../lib/api';
import { loadGoogleMaps } from '../../lib/maps';
import { useUrlPrefix } from '../../lib/useUrlPrefix';

type Location = {
  id: string;
  name: string;
  slug: string;
  address: string;
  latitude: number | null;
  longitude: number | null;
  timezone: string;
  is_virtual: number;
};

// Loose typing for the Maps JS API — we don't want to pull a full @types/google.maps
// dep for a few callsites.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type GMaps = any;

export default function LocationsMap() {
  const { orgSlug } = useParams();
  const prefix = useUrlPrefix();
  const navigate = useNavigate();
  const [locations, setLocations] = useState<Location[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const mapEl = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<GMaps | null>(null);
  const markersRef = useRef<Map<string, GMaps>>(new Map());
  const infoRef = useRef<GMaps | null>(null);
  const itemRefs = useRef<Map<string, HTMLLIElement>>(new Map());

  useEffect(() => {
    if (!orgSlug) return;
    api<Location[]>(`/book/${orgSlug}/locations`)
      .then(setLocations)
      .catch((e) => setErr(e.message || 'load_failed'));
  }, [orgSlug]);

  // Single-location orgs skip the picker entirely.
  useEffect(() => {
    if (!locations || !orgSlug) return;
    if (locations.length === 1) {
      const only = locations[0];
      if (only) {
        navigate(`${prefix}/${orgSlug}/locations/${only.slug}`, { replace: true });
      }
    }
  }, [locations, orgSlug, prefix, navigate]);

  const geocoded = useMemo(
    () =>
      (locations ?? []).filter(
        (l): l is Location & { latitude: number; longitude: number } =>
          l.latitude != null && l.longitude != null,
      ),
    [locations],
  );

  const virtualLocations = useMemo(
    () => (locations ?? []).filter((l) => l.is_virtual),
    [locations],
  );
  const physicalLocations = useMemo(
    () => (locations ?? []).filter((l) => !l.is_virtual),
    [locations],
  );

  // Build the InfoWindow content node once per click — small enough that
  // re-allocating is cheaper than wrangling a persistent React portal.
  function buildInfoContent(loc: Location): HTMLElement {
    const root = document.createElement('div');
    root.className = 'map-info';
    const title = document.createElement('div');
    title.className = 'map-info-title';
    title.textContent = loc.name;
    const addr = document.createElement('div');
    addr.className = 'map-info-addr';
    addr.textContent = loc.address;
    const btn = document.createElement('button');
    btn.className = 'btn primary map-info-btn';
    btn.textContent = 'Continue';
    btn.onclick = () => {
      navigate(`${prefix}/${orgSlug}/locations/${loc.slug}`);
    };
    root.appendChild(title);
    root.appendChild(addr);
    root.appendChild(btn);
    return root;
  }

  function selectLocation(loc: Location, source: 'pin' | 'list') {
    setSelectedId(loc.id);
    const marker = markersRef.current.get(loc.id);
    const map = mapRef.current;
    const info = infoRef.current;
    if (marker && map && info) {
      info.setContent(buildInfoContent(loc));
      info.open({ anchor: marker, map });
      if (source === 'list') {
        map.panTo(marker.getPosition());
      }
    }
    if (source === 'pin') {
      const el = itemRefs.current.get(loc.id);
      if (el) el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }

  useEffect(() => {
    const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
    if (!apiKey || geocoded.length === 0 || !mapEl.current) return;
    let cancelled = false;
    loadGoogleMaps(apiKey)
      .then((g: GMaps) => {
        if (cancelled || !mapEl.current) return;
        const center = { lat: geocoded[0]!.latitude, lng: geocoded[0]!.longitude };
        const map = new g.maps.Map(mapEl.current, {
          center,
          zoom: geocoded.length === 1 ? 14 : 11,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
        });
        mapRef.current = map;
        infoRef.current = new g.maps.InfoWindow({ pixelOffset: new g.maps.Size(0, -4) });

        const bounds = new g.maps.LatLngBounds();
        markersRef.current.clear();
        for (const loc of geocoded) {
          const position = { lat: loc.latitude, lng: loc.longitude };
          bounds.extend(position);
          const marker = new g.maps.Marker({
            position,
            map,
            title: loc.name,
          });
          marker.addListener('click', () => selectLocation(loc, 'pin'));
          markersRef.current.set(loc.id, marker);
        }
        if (geocoded.length > 1) {
          map.fitBounds(bounds);
        }
      })
      .catch((e) => console.warn('[map] load_failed', e));
    return () => {
      cancelled = true;
    };
    // selectLocation captures `navigate`, `prefix`, `orgSlug` via closure but those
    // are stable for the lifetime of this page — re-init the map only when the
    // set of geocoded locations changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geocoded]);

  if (err === 'not_found') return <h1>Organization not found</h1>;
  if (err) return <p className="err">Error: {err}</p>;
  if (!locations) return <p className="muted">Loading locations…</p>;

  return (
    <div className="locations-page">
      <h1>Pick a location</h1>

      {locations.length === 0 && <p className="muted">No active locations yet.</p>}

      {/* Virtual / online options — no map pin, shown as chips above the map. */}
      {virtualLocations.length > 0 && (
        <div className="locations-virtual">
          <h2 className="locations-virtual-title">Online</h2>
          <div className="locations-virtual-chips">
            {virtualLocations.map((l) => (
              <button
                key={l.id}
                className="btn ghost locations-virtual-chip"
                onClick={() => navigate(`${prefix}/${orgSlug}/locations/${l.slug}`)}
              >
                <Video size={14} /> {l.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {physicalLocations.length > 0 && (
        <div className="locations-layout">
          <aside className="locations-list" aria-label="Locations">
            <ul>
              {physicalLocations.map((l) => {
                const isSelected = selectedId === l.id;
                const hasCoords = l.latitude != null && l.longitude != null;
                return (
                  <li
                    key={l.id}
                    ref={(el) => {
                      if (el) itemRefs.current.set(l.id, el);
                      else itemRefs.current.delete(l.id);
                    }}
                    className={`locations-item ${isSelected ? 'is-selected' : ''}`}
                    onClick={() => {
                      if (hasCoords) selectLocation(l, 'list');
                    }}
                    onMouseEnter={() => {
                      if (hasCoords) selectLocation(l, 'list');
                    }}
                  >
                    <div className="locations-item-body">
                      <div className="locations-item-name">
                        <MapPin size={14} /> {l.name}
                      </div>
                      <div className="locations-item-addr">{l.address}</div>
                    </div>
                    <button
                      className="primary"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`${prefix}/${orgSlug}/locations/${l.slug}`);
                      }}
                    >
                      Continue
                    </button>
                  </li>
                );
              })}
            </ul>
          </aside>
          <div className="locations-map" ref={mapEl} />
        </div>
      )}
    </div>
  );
}
